import React, { useEffect, useState } from "react";
import { Heading } from "../../components/catalyst/heading";
import { PlayerDetailsViewOnly, Team } from "../../models/teamModel";
import { MatchSetupConfig } from "../../models/match";
import { TrashIcon } from "@heroicons/react/24/outline";
import { PlayerRecord } from "../../components/PlayerRecord";
import { showDate } from "../../utils/dateUtils";
import {
  Listbox,
  ListboxLabel,
  ListboxOption,
} from "../../components/catalyst/listbox";
import { CourseConfig } from "../../models/teeBox";

interface SetupPlayersForMatchProps {
  matchConfig: MatchSetupConfig;
  team?: Team;
  availableHoles: string[];
  entry: any;
  courseConfig?: CourseConfig;
  savePlayersSelections: (selectedPlayers: any) => void;
  onCancel: () => void;
}

const SetupPlayersForMatch: React.FC<SetupPlayersForMatchProps> = ({
  team,
  availableHoles,
  matchConfig,
  entry,
  savePlayersSelections,
  onCancel,
}) => {
  const [assignments, setAssignments] = useState<{
    [key: string]: PlayerDetailsViewOnly[];
  }>({});

  const [unavailablePlayers, setUnavailablePlayers] = useState<
    PlayerDetailsViewOnly[]
  >([]);

  const handleAssign = (
    hole: string,
    member: PlayerDetailsViewOnly,
    position: number
  ) => {
    const preferredDates = member.preferredDates;
    const matchDate = matchConfig.date ?? "";

    const isDateInRange = (dateRange: string) => {
      const [startDate, endDate] = dateRange.split("-");
      return startDate <= matchDate && matchDate <= endDate;
    };

    const isAvailable = preferredDates.some((date) => {
      return date.includes("-") ? isDateInRange(date) : date === matchDate;
    });

    if (isAvailable) {
      setAssignments((prev) => {
        const currentPlayers = prev[hole] || [];
        const newPlayers = [...currentPlayers];
        newPlayers[position] = member;
        return { ...prev, [hole]: newPlayers };
      });
    } else {
      alert(`${member.firstName} is not available on the preferred dates.`);
    }
  };

  const handleRemove = (hole: string, position: number) => {
    setAssignments((prev) => {
      const currentPlayers = prev[hole] || [];
      return {
        ...prev,
        [hole]: currentPlayers.filter((_, index) => index !== position),
      };
    });
  };

  const getAvailableMembers = () => {
    const assignedMemberIds = Object.values(assignments)
      .flat()
      .map((member) => member?.playerId);
    const unAvailablePlayerIds = unavailablePlayers.map((a) => a.playerId);
    return team?.playersColl.filter(
      (member) =>
        !assignedMemberIds.includes(member.playerId) &&
        !unAvailablePlayerIds.includes(member.playerId)
    );
  };

  const submitPlayerSelections = () => {
    if (Object.keys(assignments).length === 0) {
      return;
    }
    const selectedPlayers = Object.entries(assignments).flatMap(
      ([hole, members]) => {
        const teeBox = matchConfig.teeboxes?.find((h) => h.desc === hole);
        return members.map((member) => ({
          playerId: member?.playerId,
          holeNumber: teeBox?.number ?? 0,
          holeDescription: hole,
          dateTime: `${matchConfig.date ?? ""}${
            teeBox?.time ?? matchConfig.time
          }`,
        }));
      }
    );

    const payload = { teamId: team?.id ?? "", players: selectedPlayers };
    savePlayersSelections(payload);
  };

  const isSubmitDisabled = () => {
    if (Object.keys(assignments).length !== availableHoles.length) {
      return true;
    }
    const requiredPlayers = matchConfig.playFormat === "doubles" ? 2 : 1;
    return Object.values(assignments).some(
      (members) => members.length !== requiredPlayers
    );
  };

  useEffect(() => {
    const playersNotAvailable = getPlayersNotAvailable() || [];
    setUnavailablePlayers(playersNotAvailable);

    const initialAssignments: { [key: string]: PlayerDetailsViewOnly[] } = {};

    const teamSelectedPlayers = matchConfig.playerSelections?.find(
      (m) => m.teamId === team?.id
    );
    teamSelectedPlayers?.players.forEach((player) => {
      const hole = player.holeDescription;
      const member = team?.playersColl.find(
        (m) => m.playerId === player.playerId
      );
      if (member) {
        initialAssignments[hole] = initialAssignments[hole] || [];
        initialAssignments[hole].push(member);
      }
    });
    setAssignments(initialAssignments);
  }, [team, matchConfig]);

  const getPlayersNotAvailable = () => {
    return team?.playersColl.filter((member) => {
      const preferredDates = member.preferredDates;
      const matchDate = matchConfig.date ?? "";

      const isDateInRange = (dateRange: string) => {
        const [startDate, endDate] = dateRange.split("-");
        return startDate <= matchDate && matchDate <= endDate;
      };

      const isAvailable = preferredDates?.some((date) => {
        return date.includes("-") ? isDateInRange(date) : date === matchDate;
      });

      return !isAvailable;
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center">
        <Heading>Assign Players to Tee Boxes</Heading>
        <button
          onClick={onCancel}
          className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
        >
          ← Back
        </button>
      </div>
      <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-md mb-6">
        <h2 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">
          Match Details
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Match Type
            </p>
            <p className="text-sm text-gray-900 dark:text-gray-100">
              {entry.matchType}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Date
            </p>
            <p className="text-sm text-gray-900 dark:text-gray-100">
              {showDate(entry.date)}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Location
            </p>
            <p className="text-sm text-gray-900 dark:text-gray-100">
              {entry.location}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Tee Off Time
            </p>
            <p className="text-sm text-gray-900 dark:text-gray-100">
              {entry.teeOff}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              Stage
            </p>
            <p className="text-sm text-gray-900 dark:text-gray-100">
              {entry.groupOrKnockout}
            </p>
          </div>
        </div>
      </div>
      <div className="space-y-4 bg-white dark:bg-gray-800 p-1 rounded-sm shadow-md">
        <h2 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100">
          Assign Players
        </h2>
        {unavailablePlayers.length > 0 && (
          <div className="bg-red-100 dark:bg-red-700 p-4 rounded-sm mb-6">
            <h3 className="text-lg font-semibold mb-3 text-red-900 dark:text-red-100">
              Player(s) not available on match day
            </h3>
            <ul className="list-disc pl-5">
              {unavailablePlayers.map((member) => (
                <PlayerRecord key={member.playerId} member={member} />
              ))}
            </ul>
          </div>
        )}

        {availableHoles &&
          availableHoles.map((hole, index) => (
            <div
              key={hole}
              className="p-1 rounded-sm shadow-sm bg-gray-100 dark:bg-gray-700 max-w-3xl mx-auto"
            >
              <div className="flex items-center space-x-4 flex-wrap">
                <h4 className="font-semibold mb-2 text-gray-900 dark:text-gray-100">
                  <span className="font-normal text-sm text-gray-700 dark:text-gray-300">
                    {index + 1}. Tee Box{" "}
                  </span>
                  {hole}
                </h4>
                {assignments[hole]?.map((player, playerIndex) => (
                  <div
                    key={playerIndex}
                    className="flex items-center space-x-4"
                  >
                    <PlayerRecord member={player} />
                    <button
                      onClick={() => handleRemove(hole, playerIndex)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>
              {(!assignments[hole] ||
                assignments[hole].length <
                  (matchConfig.playFormat === "doubles" ? 2 : 1)) && (
                <div className="flex items-center space-x-4">
                  <div className="w-1/2">
                    <Listbox
                      onChange={(memberId) => {
                        const member = team?.playersColl.find(
                          (m) => m.playerId === memberId
                        );
                        if (member) {
                          handleAssign(
                            hole,
                            member,
                            assignments[hole]?.length || 0
                          );
                        }
                      }}
                      value=""
                    >
                      <ListboxOption value="">
                        <ListboxLabel>Select a player</ListboxLabel>
                      </ListboxOption>
                      {getAvailableMembers()?.map((member) => (
                        <ListboxOption
                          key={member.playerId}
                          value={member.playerId}
                        >
                          <div className="flex w-full h-8">
                            <PlayerRecord member={member} />
                          </div>
                        </ListboxOption>
                      ))}
                    </Listbox>
                  </div>
                </div>
              )}
            </div>
          ))}
        <div className="flex justify-end mt-4">
          <button
            onClick={submitPlayerSelections}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            disabled={isSubmitDisabled()}
          >
            Submit Player Selections
          </button>
        </div>
      </div>
    </div>
  );
};

export default SetupPlayersForMatch;
